import { Users, Wallet, IndianRupee, TrendingUp, CheckCircle2, XCircle, Landmark, Percent, Clock } from "lucide-react";
import { inr } from "@/lib/api";
import { Skeleton, PenguinIcon } from "@/components/common";
import { useAnalytics, Toolbar, Kpi, Panel, TrendArea, Bars, Donut, RankTable, CHART_COLORS, inrShort } from "@/lib/adminUI";

export default function AdminDashboard() {
  const a = useAnalytics();
  if (!a.data) return <div className="space-y-4"><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div><Skeleton className="h-72" /></div>;
  const o = a.data.overview;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">Executive Overview</h1><p className="text-sm text-slate-500">Real-time portfolio & collection performance{a.demo && " • includes test data"}.</p></div>
        <Toolbar a={a} />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total Collected" value={inrShort(o.total_collected)} delta={o.window_collected_delta} hint="vs prev period" icon={IndianRupee} />
        <Kpi label="Outstanding" value={inrShort(o.outstanding)} icon={PenguinIcon} accent="#F59E0B" />
        <Kpi label="Awaiting Lender Confirmation" value={a.data.collection_intel.pending_confirmation_count} hint={inrShort(a.data.collection_intel.pending_confirmation_amount)} icon={Clock} accent="#F59E0B" />
        <Kpi label="Collection Rate" value={`${o.collection_rate}%`} icon={Percent} accent="#10B981" />
        <Kpi label="Total Customers" value={o.total_customers} icon={Users} accent="#0EA5E9" />
        <Kpi label="Active Loans" value={o.active_loans} hint={`${o.closed_loans} closed`} icon={Wallet} />
        <Kpi label="Payment Success" value={`${o.success_rate}%`} delta={o.window_success_delta} hint={`${o.payment_success} paid`} icon={CheckCircle2} accent="#10B981" />
        <Kpi label="Payment Failure" value={`${o.failure_rate}%`} hint={`${o.payment_failed} failed`} icon={XCircle} accent="#EF4444" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Collection Trend" subtitle={`${a.data.range.from} → ${a.data.range.to}`} className="lg:col-span-2">
          <TrendArea data={a.data.collections.series} dataKey="collection" />
        </Panel>
        <Panel title="Payment Status" subtitle="Success · Failed · Pending">
          <Donut data={a.data.payments.status_counts} />
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Payments Volume" subtitle="Success vs Failed per day" className="lg:col-span-2">
          <Bars data={a.data.payments.series} keys={["success", "failed"]} colors={[CHART_COLORS[0], "#EF4444"]} />
        </Panel>
        <Panel title="Outstanding Aging" subtitle="By days overdue">
          <Bars data={a.data.aging.map((x) => ({ date: x.bucket, amount: x.amount }))} keys={["amount"]} money />
        </Panel>
      </div>

      <Panel title="Lender Performance" subtitle="Collections & outstanding by lender">
        <RankTable cols={["Lender", "Loans", "Collected", "Outstanding", "Success"]}
          rows={a.data.lenders}
          render={(l) => (
            <tr key={l.lender} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-medium text-[#0B132B]">{l.lender}</td>
              <td className="px-4 py-3">{l.loans}</td>
              <td className="px-4 py-3 font-semibold text-emerald-600">{inr(l.collected)}</td>
              <td className="px-4 py-3 text-slate-600">{inr(l.outstanding)}</td>
              <td className="px-4 py-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-[#0B132B]">{l.success_rate}%</span></td>
            </tr>
          )} />
      </Panel>
    </div>
  );
}
